//changed due to package upgrade
import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Tooltip,
  List,
  alpha,
  Stack,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import BoltIcon from '@mui/icons-material/Bolt';
import jwtAxios from '@projectK/services/auth/JWT';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { BiSolidHourglass } from 'react-icons/bi';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import {
  isParent,
  isActualSuperAdmin,
  isActualAdmin,
  isSuperAdmin,
  isAdmin,
} from '@projectK/context/RolesProvider';
import LockerPage from '../../../../../../libs/modules/src/lib/account/MyProfile/LockerPage';
import MyProducts from '../../../../../../libs/modules/src/lib/account/MyProfile/MyProducts';
import ActivitySchedules from './ActivitySchedules';
import UpcomingActivities from './UpcomingActivities';
import { useMobileSettings } from '@projectK/context/MobileContextProvider';
import ActivityWaitlist from './ActivityWaitlist';
import IntlMessages from '@projectK/helpers/IntlMessages';
import InfoIcon from '@mui/icons-material/Info';
import { useDashboard } from '@projectK/context/DashboardProvider';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import AlarmIcon from '@mui/icons-material/Alarm';
import WarningIcon from '@mui/icons-material/Warning';
import { RxLightningBolt } from 'react-icons/rx';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import { FaMedal, FaTicketAlt, FaCalendarCheck } from 'react-icons/fa';
import { ReactComponent as PackageBoxIcon } from '../../../../src/assets/images/dashboard/package_box.svg';
import { timeZoneDate } from '@projectK/modules/apps/DateFormat';
import PropTypes from 'prop-types';

const MissedPaymentCard = React.lazy(() =>
  import('../../student/MissedPaymentCard'),
);

const ParentDashboard = ({ restrictDashboard }) => {//NOSONAR
  const navigate = useNavigate();
  const todayDate = timeZoneDate().todayDate;
  const { user } = useAuthUser();
  const { showDashboard } = useDashboard();
  const companyTheme = CompanyTheme();
  const verbiageData =
    JSON.parse(localStorage.getItem('verbiageSettings')) || {};
  const [totalCreditAmount, setTotalCreditAmount] = useState(0);
  const [waitListCount, setWaitListCount] = useState(0);

  const [next7DaysCount, setNext7DaysCount] = useState(0);
  const [percentChange, setPercentChange] = useState(0);

  const fetchNext7DaysSessionCount = async () => {
    try {
      const route = isParent(user)
        ? `/dashboard/parent/next7days-session-count/${user?.id}`
        : `/dashboard/count/registered-sessions/${user?.id}`;

      const response = await jwtAxios.get(route);
      const { next7DaysCount, percentChange } = response.data;
      setNext7DaysCount(next7DaysCount || 0);
      setPercentChange(percentChange || 0);
    } catch (error) {
      console.error('Error fetching next 7 days session count:', error);
      setNext7DaysCount(0);
      setPercentChange(0);
    }
  };

  useEffect(() => {
    fetchNext7DaysSessionCount();
    fetchSessionCompletedCount();
  }, [user?.id]);

  //session completed
  const [completedCount, setCompletedCount] = useState(0);
  const [totalCompletedHours, setTotalCompletedHours] = useState(0);

  const fetchSessionCompletedCount = async () => {
    try {
      const route = isParent(user)
        ? `/dashboard/sessions-completed/parent/${user?.id}`
        : `/dashboard/sessions-completed-count/${user?.id}`;

      const response = await jwtAxios.get(route);
      const { totalSessionsCompleted, totalHours } = response.data;
      setCompletedCount(totalSessionsCompleted || 0);
      setTotalCompletedHours(totalHours || 0);
    } catch (error) {
      console.error('Error fetching next 7 days session count:', error);
      setCompletedCount(0);
      setTotalCompletedHours(0);
    }
  };

  const quickActions = [
    {
      title: <IntlMessages id='sidebar.transactionHistory' />,
      icon: <TextSnippetIcon fontSize='large' />,
      bgcolor: '#2493ee',
      color: '#fff',
      link: '/transaction-history',
      subText: <IntlMessages id='checkSchedules' />,
      info: 'This is info message1',
    },
    {
      title: <IntlMessages id='searchNewProgram' />,
      icon: <SearchIcon fontSize='large' />,
      bgcolor: '#2a9d90',
      color: '#fff',
      link: `/${user?.roleMenuFlag[0]}/product-listing`,
      subText: <IntlMessages id='findPrograms' />,
      info: 'This is info message2',
    },
    {
      title: <IntlMessages id='sidebar.settings' />,
      icon: <TuneIcon fontSize='large' />,
      bgcolor: '#ec4699',
      color: '#fff',
      link: '/settings',
      subText: <IntlMessages id='manageAll' />,
      info: 'This is info message3',
    },
  ];

  const stats = [
    {
      title: <IntlMessages id='sessionRegistered' />,
      value: next7DaysCount,
      sub: <IntlMessages id='next7Days' />,
      sub2: `${percentChange}% from last week`,
      icon: <FaCalendarCheck fontSize='large' />,
      bgcolor: '#dbeafe',
      borderColor: '#2196f3',
      subColor: 'success.main',
      toolTipTitle: 'Total number of sessions registered across all children.',
    },
    {
      title: <IntlMessages id='sessionsCompleted' />,
      value: completedCount,
      sub: <IntlMessages id='currentSeason' />,
      sub2: (
        <>
          <IntlMessages id='totalHours' />: {totalCompletedHours}
        </>
      ),
      icon: <FaMedal fontSize='medium' />,
      bgcolor: '#dcfce7',
      borderColor: '#4caf50',
      toolTipTitle: 'Total number of sessions completed across all children.',
    },
    {
      title: <IntlMessages id='waitlistedSession' />,
      value: waitListCount,
      icon: <BiSolidHourglass fontSize='large' />,
      bgcolor: '#ffeed4',
      borderColor: '#ff9800',
      toolTipTitle: 'Total number of waitlist across all children.',
    },
    {
      title: <IntlMessages id='avaliableCredits' />,
      value:
        totalCreditAmount === '∞'
          ? '∞'
          : (Number(totalCreditAmount) || 0).toFixed(2),
      sub: <IntlMessages id='manageCredits' />,
      icon: <FaTicketAlt fontSize='large' />,
      bgcolor: '#f3e8ff',
      borderColor: '#9c27b0',
      link: '/transaction-history',
      toolTipTitle: 'Total number of credits across all children.',
    },
  ];

  /********************************My Products******************************/
  const [myProducts, setMyProducts] = useState([]);
  const [myProductsLoading, setMyProductsLoading] = useState(true);
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [isAdjustCreditsModalOpen, setIsAdjustCreditsModalOpen] =
    useState(false);

  // Helper: Get Top 3 Low-Credit Products
  const getTopLowCreditProducts = (products) => {
    if (!products || !products?.length) return [];

    return products
      .filter((p) => p.totalCreditAmount !== '∞')
      .map((p) => ({ ...p, numericCredit: parseFloat(p.totalCreditAmount) }))
      .filter((p) => !isNaN(p.numericCredit))
      .sort((a, b) => a.numericCredit - b.numericCredit)
      .slice(0, 3);
  };

  const topLowCreditProducts = getTopLowCreditProducts(myProducts);
  useEffect(() => {
    fetchUserProduct();
  }, [user?.id]);

  const fetchUserProduct = async () => {
    try {
      setMyProductsLoading(true);
      const route = isParent(user)
        ? `/products/productname/parent/${user?.id}`
        : `/products/productname/${user?.id}`;
      const response = await jwtAxios.get(route);
      const { data } = response;
      setMyProducts(data);
      const totalCreditAmount = (() => {
        if (!data || !data.length) return 0;

        // Separate numeric values from '∞'
        const numericValues = data
          .map((item) => parseFloat(item.totalCreditAmount))
          .filter((val) => !isNaN(val));

        const onlyInfinity = data.every(
          (item) => item.totalCreditAmount === '∞',
        );

        if (onlyInfinity) return '∞';

        // Sum only numeric values, ignore '∞'
        return numericValues.reduce((sum, val) => sum + val, 0);
      })();
      // Optional: store it in state if you want to display it in the UI
      setTotalCreditAmount(totalCreditAmount);
      setMyProductsLoading(false);
    } catch (error) {
      setMyProducts([]);
      setMyProductsLoading(false);
    }
  };

  const openAddProductsModal = () => {
    setIsAddProductModalOpen(true);
  };
  const openAdjustCreditsModal = () => {
    setIsAdjustCreditsModalOpen(true);
  };

  /********************************End of My Products******************************/

  //********************************ActivitySession********************************//

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(true);
  const [search, setSearch] = useState('');
  const [useDateFilter, setUseDateFilter] = useState(true);
  const { isMobile } = useMobileSettings();
  const abortControllerRef = useRef(null);
  const fetchActivitySessions = async () => {
    setLoading(true);
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;
    try {
      const routeDate = isParent(user)
        ? '/user-members/session'
        : '/activity-session/user/session';

      const response = await jwtAxios.get(`${routeDate}/${user?.id}`, {
        params: {
          perPage: 'all',
          useDateFilter: useDateFilter,
          search,
        },
        signal,
      });
      const { data } = response;
      setList(data?.list);
      setLoading(false);
    } catch (error) {
      if (error.message !== 'canceled') {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!isMobile) {
      fetchActivitySessions();
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [useDateFilter, search, isMobile]);

  /*****************************My Schedule**************************/

  const [currentActivity, setCurrentActivity] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [useDateFilterSession, setUseDateFilterSession] = useState(false);
  const [searchSession, setSearchSession] = useState('');
  const [showAllSession, setShowAllSession] = useState(false);

  const fetchMySchedule = async () => {
    try {
      setScheduleLoading(true);
      const routeDate = isParent(user)
        ? '/user-members/active-session'
        : '/activity-session/user/active-session';

      const response = await jwtAxios.get(`${routeDate}/${user?.id}`, {
        params: {
          perPage: 'all',
          useDateFilter: useDateFilterSession,
          search: searchSession,
        },
      });
      const { data } = response;
      setCurrentActivity(data?.list);
      setScheduleLoading(false);
    } catch (error) {
      setScheduleLoading(false);
    }
  };

  useEffect(() => {
    if (!isMobile) {
      fetchMySchedule();
    }
  }, [useDateFilterSession, searchSession, isMobile]);

  /***************************Wait List******************************/
  const [waitListData, setWaitListData] = useState(null);
  const [waitListLoading, setWaitListLoading] = useState(true);

  const fetchWaitList = async () => {
    try {
      setWaitListLoading(true);
      let url;
      if (isParent(user)) {
        url = `/user-members/waitlist-session/${user?.id}`;
      } else if (isSuperAdmin(user) || isAdmin(user)) {
        url = `/user-waitlist`;
      } else {
        url = `/activity-session/user/waitlist-session/${user?.id}`;
      }
      const response = await jwtAxios.get(url, {
        params: { perPage: 'all' },
      });
      const { data } = response;
      setWaitListData(data?.list);
      setWaitListCount(Array.isArray(data?.list) ? data?.list?.length : 0);
      setWaitListLoading(false);
    } catch (error) {
      setWaitListLoading(false);
    }
  };

  useEffect(() => {
    if (!isMobile) {
      fetchWaitList();
    }
  }, [isMobile]);

  const [missedPayments, setMissedPayments] = useState(null);
  const [paymentsLoading, setPaymentsLoading] = useState(true);

  const fetchMissedPayments = async () => {
    try {
      setPaymentsLoading(true);
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      const response = await jwtAxios.get(
        `/dashboard/missed-payments/parent/student`,
        {
          params: {
            filters: { user_id: user?.id },
          },
          signal,
        },
      );
      const { data } = response;
      setMissedPayments(data?.list);
      setPaymentsLoading(false);
    } catch (error) {
      setPaymentsLoading(false);
      if (error.message !== 'canceled') {
        console.error('Error fetching payment data:', error);
      }
    }
  };

  useEffect(() => {
    fetchMissedPayments();
  }, [user?.id]);

  const renderMissedPayments = () => {
    if (paymentsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!missedPayments || missedPayments?.length === 0) {
      return (
        <Typography
          variant='body1'
          color='text.secondary'
          sx={{ textAlign: 'center', py: 3, fontWeight: '600' }}
        >
          No missed payments
        </Typography>
      );
    }

    return (
      <Stack spacing={3}>
        {missedPayments?.map((rem) => (
          <Box
            className='reminder_box'
            key={rem.id || rem?.order_invoice} // avoid using array index
            sx={{ bgcolor: rem?.bgcolor }}
          >
            <Box
              className='icon_box'
              sx={{ bgcolor: '#ffebee', color: '#e5334eff' }}
            >
              <WarningIcon />
            </Box>
            <Box flexGrow={1}>
              <Typography variant='subtitle1' fontWeight='bold'>
                Payment Alert
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                A payment has been missed for Invoice {rem?.order_invoice} under
                a {rem?.payment_plan_id != null ? 'subscription' : 'deposit'}{' '}
                plan.
              </Typography>
              <Typography variant='body2'>
                Payment Missed: {rem?.missed_payment_count}
              </Typography>
              <Typography variant='body2'>
                Missed amount: {rem?.missed_amount}
              </Typography>
              <Button
                size='small'
                variant='contained'
                color={rem.isFirst ? 'warning' : 'primary'}
                sx={{
                  textTransform: 'none',
                  bgcolor: rem.isFirst ? '#fff' : 'primary.main',
                  color: rem.isFirst ? '#f57c00' : '#fff',
                  '&:hover': {
                    bgcolor: rem.isFirst ? '#ffe0b2' : 'primary.dark',
                  },
                }}
                onClick={() => {
                  localStorage.setItem(
                    'searchByInvoiceOrder',
                    rem.order_invoice,
                  );
                  navigate(`/transaction-history`);
                }}
              >
                View
              </Button>
            </Box>
          </Box>
        ))}
      </Stack>
    );
  };

  return (
    <>
      {restrictDashboard && <MissedPaymentCard />}
      {showDashboard ? (
        <Box className='admin-dashboard player-dashboard'>
          {/* Header */}
          <Box
            className='headerbg'
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              p: 2,
            }}
          >
            <Box>
              <Typography variant='h1'>
                <IntlMessages id='welcomeBack' /> {user?.first_name}{' '}
                {user?.last_name}
              </Typography>
              <Typography sx={{ color: '#eee', mb: 6 }}>
                <IntlMessages id='parentandPlayerTitle' />
              </Typography>
            </Box>
            {/* Right Content */}
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography
                variant='body2'
                sx={{ color: '#eee', fontWeight: 'bold', mb: 3 }}
              >
                Today
                <Typography>{todayDate.toFormat('MMMM dd, yyyy')}</Typography>
              </Typography>
            </Box>
          </Box>

          {/* Stats Section */}
          <Grid container spacing={5} className='stats'>
            {stats?.map((item, index) => (
              <Grid size={{ xs: 6, sm: 6, md: 3 }} key={index + 1}>
                <Card
                  sx={{
                    borderRadius: 3,
                    border: '2px solid',
                    borderColor: item.borderColor,
                  }}
                >
                  <CardContent>
                    {/* Title + Icon */}
                    <Box
                      className='title_icon'
                      display='flex'
                      justifyContent='space-between'
                      alignItems='center'
                    >
                      <Typography
                        variant='subtitle1'
                        color='text.secondary'
                        sx={{ fontSize: '16px' }}
                      >
                        {item.title}
                      </Typography>
                      <Box
                        sx={{ color: item.borderColor, bgcolor: item.bgcolor }}
                      >
                        {item.icon}
                      </Box>
                    </Box>

                    {/* Main Value */}
                    <Typography
                      variant='h4'
                      sx={{
                        fontWeight: 'bold',
                        mb: 1,
                      }}
                    >
                      {item.value}
                    </Typography>
                    {item.link ? (
                      <Typography
                        component='a'
                        href={item.link}
                        variant='h4'
                        sx={{
                          mb: 0.5,
                          color: 'primary.main',
                          fontWeight: 500,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {item.sub}
                      </Typography>
                    ) : (
                      <Typography variant='h5' color='text.secondary'>
                        {item.sub}
                      </Typography>
                    )}
                    {item.sub2 && (
                      <Typography
                        variant='h5'
                        sx={{
                          color: item.subColor || 'success.main',
                        }}
                      >
                        {item.sub2}
                      </Typography>
                    )}
                    {isParent(user) && (
                      <Tooltip className='info' title={item?.toolTipTitle}>
                        <InfoIcon fontSize='small' style={{ color: 'gray' }} />
                      </Tooltip>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Quick Actions Section */}
          <Box className='quick-actions' sx={{ mt: 5 }}>
            <Typography
              variant='h2'
              sx={{
                fontWeight: 'bold',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <RxLightningBolt fontSize='23' color='#0A8FDC' />
              <IntlMessages id='quickActions' />
            </Typography>

            <Grid container spacing={5}>
              {/* Static Quick Actions */}
              {quickActions?.map((action, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={`qa-${index + 0}`}>
                  <Card
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-2px)',
                      },
                      transition: '0.3s',
                    }}
                    onClick={() => navigate(action.link)}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        bgcolor: action.bgcolor,
                        color: action.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      {action.icon}
                    </Box>
                    <Typography variant='h4' fontWeight='bold' gutterBottom>
                      {action.title}
                    </Typography>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      gutterBottom
                    >
                      {action.subText}
                    </Typography>

                    {action?.buttonText && (
                      <Button
                        variant='contained'
                        size='small'
                        sx={{ bgcolor: action.buttonColor }}
                      >
                        {action.buttonText}
                      </Button>
                    )}
                  </Card>
                </Grid>
              ))}

              {/* Dynamic Top-Up Quick Actions */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                    },
                    transition: '0.3s',
                  }}
                >
                  {topLowCreditProducts?.length > 0 ? (
                    <Slider
                      dots={true}
                      infinite={false}
                      speed={500}
                      slidesToShow={1} // show one card at a time
                      slidesToScroll={1}
                      arrows
                    >
                      {topLowCreditProducts?.map((product, index) => (
                        <>
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: '50%',
                              bgcolor: '#f97415',
                              color: '#fff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mb: 2,
                            }}
                          >
                            <RxLightningBolt fontSize='large' />
                          </Box>
                          <Typography
                            variant='h5'
                            sx={{ fontWeight: 'bold', mb: 2 }}
                          >
                            {product?.product_name}
                          </Typography>
                          <Typography
                            variant='body2'
                            color='text.secondary'
                            gutterBottom
                          >
                            {product?.totalCreditAmount} credits remaining
                          </Typography>
                          <Button
                            variant='contained'
                            size='small'
                            sx={{ bgcolor: '#f97415' }}
                            onClick={() =>
                              navigate(
                                `/product-details/${product?.productSlug}`,
                              )
                            }
                          >
                            Top-up now!
                          </Button>
                        </>
                      ))}
                    </Slider>
                  ) : (
                    <>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          bgcolor: '#fff3e0',
                          color: '#fb8c00',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        <BoltIcon fontSize='large' />
                      </Box>
                      <Typography
                        variant='h5'
                        sx={{ fontWeight: 'bold', mb: 2 }}
                      >
                        Hurray!
                      </Typography>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        gutterBottom
                      >
                        No low-credit products
                      </Typography>
                    </>
                  )}
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Recent Activity & Updates */}
          <Grid
            container
            spacing={7}
            className='activity-updates parent_schedule_sessions'
          >
            <Grid size={{ xs: 12, md: 8 }}>
              <ActivitySchedules
                fetchActivitySessions={fetchActivitySessions}
                list={currentActivity}
                loading={scheduleLoading}
                fetchMySchedule={fetchMySchedule}
                fetchUserProduct={fetchUserProduct}
                setDateFilter={setUseDateFilterSession}
                useDateFilter={useDateFilterSession}
                setSearchQuery={setSearchSession}
                searchSession={searchSession}
                setShowAll={setShowAllSession}
                showAll={showAllSession}
                fetchNext7DaysSessionCount={fetchNext7DaysSessionCount}
                setLoading={setScheduleLoading}
              />

              <UpcomingActivities
                list={list}
                loading={loading}
                setLoading={setLoading}
                setList={setList}
                setDateFilter={setUseDateFilter}
                setSearchQuery={setSearch}
                setShowAll={setShowAll}
                showAll={showAll}
                useDateFilter={useDateFilter}
                fetchActivitySessions={fetchActivitySessions}
                fetchMySchedule={fetchMySchedule}
                fetchWaitList={fetchWaitList}
                fetchUserProduct={fetchUserProduct}
                fetchNext7DaysSessionCount={fetchNext7DaysSessionCount}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Box mb={5} className='productslist'>
                <Box
                  className='products_header'
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: {
                      xs: 'column',
                      sm: 'row',
                      md: 'column',
                      lg: 'column',
                      xl: 'row',
                    },
                    alignItems: {
                      xs: 'flex-start',
                      sm: 'center',
                      md: 'flex-start !important',
                      lg: 'flex-start',
                      xl: 'center',
                    },
                    mb: 3,
                  }}
                >
                  <Typography
                    variant='h2'
                    sx={{
                      fontWeight: 'bold',
                      mb: { xs: 1, sm: 1, md: 1, lg: 1, xl: 0 },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <PackageBoxIcon />
                    <IntlMessages id='common.myProducts' />
                  </Typography>
                  {(isActualSuperAdmin(user) || isActualAdmin(user)) && (
                    <Box
                      display='flex'
                      flexDirection='row'
                      alignItems='center'
                      mb={1}
                    >
                      <Button
                        size='small'
                        startIcon={<AddIcon />}
                        variant='outlined'
                        onClick={openAddProductsModal}
                        sx={{
                          borderColor: companyTheme
                            ? companyTheme.primary.main
                            : '#2196f3',
                          '&:hover': {
                            borderColor: companyTheme
                              ? alpha(companyTheme.primary.main, 0.8)
                              : alpha('#2196f3', 0.8),
                          },
                          /*                      color: companyTheme
                        ? companyTheme.primary.main
                        : 'primary', */
                        }}
                      >
                        Add {verbiageData?.product_type || 'Product'}
                      </Button>
                      <Button
                        size='small'
                        startIcon={<AddIcon />}
                        sx={{
                          ml: 1.5,
                          borderColor: companyTheme
                            ? companyTheme.primary.main
                            : '#2196f3',
                          '&:hover': {
                            borderColor: companyTheme
                              ? alpha(companyTheme.primary.main, 0.8)
                              : alpha('#2196f3', 0.8),
                          },
                          /*                      color: companyTheme
                        ? companyTheme.primary.main
                        : 'primary', */
                        }}
                        variant='outlined'
                        onClick={openAdjustCreditsModal}
                      >
                        Adjust Credits
                      </Button>
                    </Box>
                  )}
                </Box>

                <Card className='products-card'>
                  <CardContent sx={{ p: 0 }}>
                    <List disablePadding>
                      <MyProducts
                        loading={myProductsLoading}
                        setLoading={setMyProductsLoading}
                        settings={myProducts}
                        fetchUserProduct={fetchUserProduct}
                        isModalOpen={isAddProductModalOpen}
                        setIsModalOpen={setIsAddProductModalOpen}
                        openModal={openAddProductsModal}
                        setIsAdjustCreditsModalOpen={
                          setIsAdjustCreditsModalOpen
                        }
                        isAdjustCreditsModalOpen={isAdjustCreditsModalOpen}
                        showDashboard={showDashboard}
                        fetchMySchedule={fetchMySchedule}
                        fetchWaitList={fetchWaitList}
                        fetchActivitySessions={fetchActivitySessions}
                      />
                    </List>
                  </CardContent>
                </Card>
              </Box>

              <Box mb={5} className='waitlistlist'>
                {/* Waitlist */}
                <Typography variant='h2' fontWeight='bold' mb={5}>
                  <IntlMessages id='waitListStatus' />
                </Typography>
                <ActivityWaitlist
                  waitListData={waitListData}
                  loading={waitListLoading}
                  fetchWaitList={fetchWaitList}
                  fetchActivitySessions={fetchActivitySessions}
                  fetchUserProduct={fetchUserProduct}
                  setLoading={setWaitListLoading}
                />
              </Box>
              {/* Reminders */}
              <Box className='reminders'>
                <Typography
                  variant='h2'
                  sx={{
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <AlarmIcon color='primary' />
                  Reminders
                </Typography>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Stack spacing={3}>{renderMissedPayments()}</Stack>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <LockerPage />
      )}
    </>
  );
};

export default ParentDashboard;

ParentDashboard.propTypes = {
  restrictDashboard: PropTypes.bool.isRequired,
};
